import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Typography, Tabs, message, Button, Row, Col, Input, Space, Select, Tag, Statistic, Flex } from 'antd';
import { InboxOutlined, AppstoreOutlined, PlusOutlined, SearchOutlined, FilterOutlined, ClearOutlined } from '@ant-design/icons';
import axios from 'axios';
import ProductList from './Inventory/ProductList';
import ProductForm from './Inventory/ProductForm';
import StockAdjust from './Inventory/StockAdjust';
import ProductCategorySelect from '../components/ProductCategorySelect';
import CategoryManager from './Inventory/CategoryManager';
import useProductStore from '../store/useProductStore';
import EmptyState from '../components/EmptyState';
import { ShoppingOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const Inventory = () => {
    const { products, loading, fetchProducts } = useProductStore();

    // Filters
    const [searchText, setSearchText] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [stockStatus, setStockStatus] = useState('all');
    const [lastValidFilters, setLastValidFilters] = useState({ searchText: '', selectedCategory: 'all', stockStatus: 'all' });

    // Barcode Scanner Focus Lock Logic
    const searchInputRef = useRef(null);
    const lastKeyTimeRef = useRef(0);
    const isScanningRef = useRef(false);
    const scanTimeoutRef = useRef(null);

    // Modal State
    const [modalMode, setModalMode] = useState(null); // 'create', 'edit', 'stock'
    const [currentItem, setCurrentItem] = useState(null);
    const [categoryManagerVisible, setCategoryManagerVisible] = useState(false);

    useEffect(() => {
        fetchProducts();
    }, []);

    // Local Filtering and Logic Validation
    const { filteredProducts, emptyReason, emptyResultExpected, isBlocked } = useMemo(() => {
        const search = searchText.toLowerCase();

        // 1. Validate if search term matches ANY product in the database (Rule 1)
        const someMatchSearchGlobal = products.some(p =>
            (p.ref || '').toLowerCase().includes(search) ||
            (p.label || '').toLowerCase().includes(search) ||
            (p.barcode || '').toLowerCase().includes(search)
        );

        // 2. Validate if category has ANY products
        const productsInCategory = products.filter(p =>
            selectedCategory === 'all' ? true :
                selectedCategory === 'uncategorized' ? !p.category_id :
                    p.category_id == selectedCategory
        );
        const categoryHasProducts = productsInCategory.length > 0;

        // 3. Execution Blocking Logic (Rule 3)
        let blockingReason = null;
        let blocked = false;

        if (products.length > 0) {
            if (searchText && !someMatchSearchGlobal) {
                // Not necessarily blocked, just empty search result
            } else if (searchText && someMatchSearchGlobal && productsInCategory.length > 0) {
                const someMatchInCat = productsInCategory.some(p =>
                    (p.ref || '').toLowerCase().includes(search) ||
                    (p.label || '').toLowerCase().includes(search) ||
                    (p.barcode || '').toLowerCase().includes(search)
                );
                if (!someMatchInCat) {
                    blockingReason = `The search term "${searchText}" matches items, but none exist in the selected category.`;
                    blocked = true;
                }
            } else if (selectedCategory !== 'all' && !categoryHasProducts) {
                blockingReason = "The selected category is currently empty. Filtering is blocked to prevent confusion.";
                blocked = true;
            }
        }

        const results = products.filter(p => {
            const matchesSearch =
                (p.ref || '').toLowerCase().includes(search) ||
                (p.label || '').toLowerCase().includes(search) ||
                (p.barcode || '').toLowerCase().includes(search);

            const matchesCategory =
                selectedCategory === 'all' ? true :
                    selectedCategory === 'uncategorized' ? !p.category_id :
                        p.category_id == selectedCategory;

            const matchesStock =
                stockStatus === 'all' ? true :
                    stockStatus === 'instock' ? p.stock > 0 :
                        p.stock <= 0;

            return matchesSearch && matchesCategory && matchesStock;
        });

        // Determine clear system message for empty results (Rule 2)
        let reason = blockingReason;
        if (!blocked && results.length === 0 && products.length > 0) {
            if (searchText && !someMatchSearchGlobal) {
                reason = "No items match the search text across all categories.";
            } else if (stockStatus !== 'all' && productsInCategory.length > 0) {
                // Check if there are ANY items in this category with this stock status
                const hasAnyWithStatus = productsInCategory.some(p => stockStatus === 'instock' ? p.stock > 0 : p.stock <= 0);
                if (!hasAnyWithStatus) {
                    reason = `There are no ${stockStatus === 'instock' ? 'in-stock' : 'out-of-stock'} items in this category.`;
                } else {
                    reason = "No items match the combined filters.";
                }
            } else {
                reason = "No items match the selected filters.";
            }
        }

        const isExpected = (searchText && !someMatchSearchGlobal);

        return { filteredProducts: results, emptyReason: reason, emptyResultExpected: isExpected, isBlocked: blocked };
    }, [products, searchText, selectedCategory, stockStatus]);

    // Preserve last valid filters in memory (Rule 4)
    useEffect(() => {
        if (filteredProducts.length > 0) {
            setLastValidFilters({ searchText, selectedCategory, stockStatus });
        }
    }, [filteredProducts, searchText, selectedCategory, stockStatus]);

    const revertToLastValid = () => {
        setSearchText(lastValidFilters.searchText);
        setSelectedCategory(lastValidFilters.selectedCategory);
        setStockStatus(lastValidFilters.stockStatus);
    };

    const handleAdd = () => {
        setModalMode('create');
        setCurrentItem(null);
    };

    const handleEdit = (record) => {
        setModalMode('edit');
        setCurrentItem(record);
    };

    const handleStock = (record) => {
        setModalMode('stock');
        setCurrentItem(record);
    };

    const handleCloseModal = () => {
        setModalMode(null);
        setCurrentItem(null);
    };

    const handleSuccess = () => {
        handleCloseModal();
        fetchProducts();
    };

    const clearFilters = () => {
        setSearchText('');
        setSelectedCategory('all');
        setStockStatus('all');
    };

    return (
        <div style={{ padding: '24px' }}>
            <Flex justify="space-between" align="center" style={{ marginBottom: 32 }}>
                <div>
                    <div className="section-title">Logistics</div>
                    <Title level={2} style={{ margin: 0, fontWeight: 700 }}>Inventory Hub</Title>
                    <Text type="secondary">Product lookup, category management, and stock adjustments</Text>
                </div>
                <Space size="middle">
                    <Button icon={<FilterOutlined />} onClick={() => setCategoryManagerVisible(true)}>Manage Categories</Button>
                    <Button type="primary" size="large" icon={<PlusOutlined />} onClick={handleAdd} style={{ height: '48px', padding: '0 24px' }}>
                        Add New Product
                    </Button>
                </Space>
            </Flex>

            <Card className="premium-card" style={{ marginBottom: 24 }} bodyStyle={{ padding: '24px' }}>
                <Row gutter={[16, 16]} align="bottom">
                    <Col xs={24} md={6}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Search</Text>
                        <Input
                            id="main-inventory-search"
                            ref={searchInputRef}
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Ref, Label, or Barcode"
                            value={searchText}
                            onChange={e => {
                                const now = Date.now();
                                const diff = now - lastKeyTimeRef.current;
                                lastKeyTimeRef.current = now;

                                // Detect fast input (scanner usually < 30ms between chars)
                                if (diff < 50) {
                                    isScanningRef.current = true;
                                    if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
                                    scanTimeoutRef.current = setTimeout(() => {
                                        isScanningRef.current = false;
                                    }, 500); // 500ms window of "scanning"
                                }

                                setSearchText(e.target.value);
                            }}
                            onFocus={() => {
                                if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
                                isScanningRef.current = false;
                            }}
                            onBlur={(e) => {
                                // If we are in a scan window and losing focus, steal it back
                                if (isScanningRef.current) {
                                    setTimeout(() => {
                                        searchInputRef.current?.focus();
                                    }, 10);
                                }
                            }}
                            onKeyDown={e => {
                                // If Enter or Tab while scanning, prevent default movement
                                if ((e.key === 'Enter' || e.key === 'Tab') && isScanningRef.current) {
                                    e.preventDefault();
                                }
                            }}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} md={5}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Category</Text>
                        <ProductCategorySelect
                            value={selectedCategory === 'all' ? undefined : selectedCategory}
                            onChange={val => setSelectedCategory(val || 'all')}
                            placeholder="All Categories"
                        />
                    </Col>
                    <Col xs={24} md={4}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Stock Status</Text>
                        <Select value={stockStatus} onChange={setStockStatus} style={{ width: '100%' }}>
                            <Option value="all">All Status</Option>
                            <Option value="instock">In Stock</Option>
                            <Option value="outstock">Out of Stock</Option>
                        </Select>
                    </Col>
                    <Col xs={24} md={5}>
                        <Space size="middle">
                            <Button icon={<ClearOutlined />} onClick={clearFilters}>Reset</Button>
                            <div style={{ borderLeft: '1px solid #f0f0f0', height: 32, paddingLeft: 16 }}>
                                <Statistic value={filteredProducts.length} suffix={`/ ${products.length}`} valueStyle={{ fontSize: '18px' }} />
                                <Text type="secondary" style={{ fontSize: '12px' }}>Products</Text>
                            </div>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                {filteredProducts.length > 0 ? (
                    <ProductList
                        products={filteredProducts}
                        loading={loading}
                        onEdit={handleEdit}
                        onStock={handleStock}
                        onRefresh={fetchProducts}
                    />
                ) : (
                    <div style={{ padding: '40px 0' }}>
                        <EmptyState
                            message={emptyReason || "No products found"}
                            description={isBlocked ? "Filter logic is blocked due to contradictory selections." : (emptyResultExpected ? "This filter combination is expected to be empty." : "Try adjusting your filters or revert to the last valid state.")}
                            icon={<ShoppingOutlined />}
                        />
                        <div style={{ textAlign: 'center', marginTop: -20, marginBottom: 40 }}>
                            <Button type="primary" ghost onClick={revertToLastValid}>
                                {isBlocked ? "Restore Last Valid State" : "Revert to Last Valid Results"}
                            </Button>
                        </div>
                    </div>
                )}
            </Card>


            <CategoryManager
                visible={categoryManagerVisible}
                onCancel={() => setCategoryManagerVisible(false)}
                onRefreshNeeded={fetchProducts}
            />

            <ProductForm
                visible={modalMode === 'create' || modalMode === 'edit'}
                mode={modalMode === 'create' ? 'create' : 'edit'}
                initialValues={currentItem}
                onCancel={handleCloseModal}
                onSuccess={handleSuccess}
            />

            <StockAdjust
                visible={modalMode === 'stock'}
                product={currentItem}
                onCancel={handleCloseModal}
                onSuccess={handleSuccess}
            />
        </div>
    );
};

export default Inventory;
